import { GrammarCheckResult } from './types';

export interface GrammarCheckerAnalytics {
  trackEvent: (event: string, data?: Record<string, any>) => void;
}

export interface GrammarCheckerErrorLogger {
  captureMessage: (message: string, level?: string) => void;
  logError: (error: Error) => void;
}

export interface GrammarCheckerConfig {
  /** Base URL for the Linguix proxy API (e.g. 'https://api.justdone.ai') */
  apiBaseUrl?: string;

  /** Returns the access token for API authorization */
  getAccessToken?: () => string | Promise<string>;

  /**
   * Custom grammar check function. If provided, apiBaseUrl and getAccessToken are ignored.
   * The module will call this function instead of the built-in API client.
   */
  checkText?: (text: string) => Promise<GrammarCheckResult>;

  /** Analytics service for tracking events (optional, noop if not provided) */
  analytics?: GrammarCheckerAnalytics;

  /** Error logger service (optional, falls back to console.warn) */
  errorLogger?: GrammarCheckerErrorLogger;

  /** Debounce delay in ms for grammar check API calls (default: 300) */
  debounceMs?: number;
}

const noopAnalytics: GrammarCheckerAnalytics = {
  trackEvent: () => {},
};

const consoleErrorLogger: GrammarCheckerErrorLogger = {
  captureMessage: (message: string) => console.warn(message),
  logError: (error: Error) => console.error(error),
};

export const resolveAnalytics = (
  config: GrammarCheckerConfig,
): GrammarCheckerAnalytics => {
  return config.analytics ?? noopAnalytics;
};

export const resolveErrorLogger = (
  config: GrammarCheckerConfig,
): GrammarCheckerErrorLogger => {
  return config.errorLogger ?? consoleErrorLogger;
};
